import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { t } from "i18next";
import { chunk, cloneDeep, get, isEmpty, map, merge, set, size, uniqBy, values } from "lodash";
import { useStoreState } from "pullstate";
import React, { useEffect, useState } from "react";
import useMedia, { MediaSizes } from "../../hooks/useMedia";
import { trackEvent } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../utils/mixpanelEvents";
import Store from "../../utils/Store";
import uploadService from "./services/uploadService";
import UploadCompleteModal from "./UploadCompleteModal";
import UploadModal from "./UploadModal";
import UploadProgressModal from "./UploadProgressModal";
import { defaultFormData } from "./utils";
var isLC = process.env.NEXT_PUBLIC_IS_LC === "true";
var DicomUpload = function DicomUpload(_ref) {
  var currentPatient = _ref.currentPatient;
  var _useState = useState(false),
    isUploadPopupOpen = _useState[0],
    toggleUploadPopup = _useState[1];
  var _useState2 = useState({}),
    selectedFiles = _useState2[0],
    setSelectedFiles = _useState2[1];
  var _useState3 = useState(false),
    isUploadInProgress = _useState3[0],
    setUploadInProgress = _useState3[1];
  var _useState4 = useState(false),
    uploadComplete = _useState4[0],
    setUploadComplete = _useState4[1];
  var _useState5 = useState(0),
    uploadSuccessCount = _useState5[0],
    setUploadSuccessCount = _useState5[1];
  var _useState6 = useState(null),
    selectedSource = _useState6[0],
    setSelectedSource = _useState6[1];
  var totalFilesCount = size(selectedFiles);
  var _useState7 = useState(defaultFormData),
    patient = _useState7[0],
    setPatient = _useState7[1];
  var _useStoreState = useStoreState(Store, function (store) {
      return {
        isOpen: isMobile ? true : store.uploadModal.isOpen,
        hasCurrentPatient: store.uploadModal.hasCurrentPatient
      };
    }),
    isOpen = _useStoreState.isOpen,
    hasCurrentPatient = _useStoreState.hasCurrentPatient;
  var isMobile = useMedia([MediaSizes.MOBILE], [true], false) && !hasCurrentPatient;
  var onClose = function onClose() {
    setUploadComplete(false);
    toggleUploadPopup(false);
    setPatient(defaultFormData);
  };
  useEffect(function () {
    /* To show warning before closing the tab if upload is in progress */
    if (isUploadInProgress) {
      window.onbeforeunload = function () {
        return t("unsaved_changes_lost_msg"); // Custom message not supported in some of the modern browsers.
      };
    } else {
      window.onbeforeunload = null;
    }
  }, [isUploadInProgress]);
  var onProgress = function onProgress(fileKey, uploadStatus) {
    setSelectedFiles(function (prevState) {
      var oldSelectedObject = cloneDeep(prevState);
      var fileObject = cloneDeep(prevState[fileKey]);
      fileObject.uploadProgress = uploadStatus;
      return merge(oldSelectedObject, _defineProperty({}, fileKey, fileObject));
    });
  };
  var sendFileForUpload = function sendFileForUpload(chunkedFiles, workspaceId) {
    return new Promise(function (resolve) {
      var promises = map(chunkedFiles, function (fileObject) {
        if (fileObject.isValidated) {
          var _get, _get2, _get3, _get4, _get5, _get6, _fileObject$file, _fileObject$fileKey;
          var patientFirstName = String((_get = get(patient, ["additional", "patient_name", "first"], "")) !== null && _get !== void 0 ? _get : "").trim() || String((_get2 = get(fileObject, ["metaData", "PatientFirstName"], "")) !== null && _get2 !== void 0 ? _get2 : "").trim();
          var patientMiddleName = String((_get3 = get(patient, ["additional", "patient_name", "middle"], "")) !== null && _get3 !== void 0 ? _get3 : "").trim() || String((_get4 = get(fileObject, ["metaData", "PatientMiddleName"], "")) !== null && _get4 !== void 0 ? _get4 : "").trim();
          var patientLastName = String((_get5 = get(patient, ["additional", "patient_name", "last"], "")) !== null && _get5 !== void 0 ? _get5 : "").trim() || String((_get6 = get(fileObject, ["metaData", "PatientLastName"], "")) !== null && _get6 !== void 0 ? _get6 : "").trim();
          var metaData = _objectSpread(_objectSpread({}, fileObject.metaData || {}), isLC ? _objectSpread(_objectSpread(_objectSpread({}, patientFirstName ? {
            patientFirstName: patientFirstName
          } : {}), patientMiddleName ? {
            patientMiddleName: patientMiddleName
          } : {}), patientLastName ? {
            patientLastName: patientLastName
          } : {}) : {});
          return uploadService({
            file: (_fileObject$file = fileObject.file) !== null && _fileObject$file !== void 0 ? _fileObject$file : new File([], "placeholder"),
            fileKey: (_fileObject$fileKey = fileObject.fileKey) !== null && _fileObject$fileKey !== void 0 ? _fileObject$fileKey : "",
            metaData: metaData,
            anonymize_dicom_metadata: fileObject.anonymize_dicom_metadata,
            onProgress: onProgress,
            workspaceId: workspaceId
          });
        }
      });
      Promise.allSettled(promises).then(function (settledResult) {
        var successCount = 0;
        var failureCount = 0;
        var result = settledResult.reduce(function (acc, res) {
          var responseObj = get(res, ["value"], null);
          var fileKey = get(responseObj, [1]);
          var status = get(res, ["status"]);
          var reasonObj = get(res, ["reason"]);
          if (status === "fulfilled") {
            var hasCreated = true;
            if (!hasCreated) {
              failureCount += 1;
              acc[fileKey] = _objectSpread(_objectSpread({}, acc[fileKey]), {}, {
                inValidMessage: "duplicate_dicom_imported"
              });
            } else {
              successCount += 1;
              acc[fileKey] = _objectSpread(_objectSpread({}, acc[fileKey]), {}, {
                uploadComplete: true
              });
            }
          } else {
            failureCount += 1;
            fileKey = get(reasonObj, ["1"], "");
            var reason = get(reasonObj, ["0", "response", "data", "message"], t("error_importing_dicom"));
            var errorCode = get(reasonObj, ["0", "response", "data", "error_code"], 0);
            acc[fileKey] = _objectSpread(_objectSpread({}, acc[fileKey]), {}, {
              inValidMessage: reason,
              errorCode: errorCode
            });
          }
          return acc;
        }, {});
        if (!isEmpty(result)) {
          setSelectedFiles(function (prevState) {
            return merge(prevState, result);
          });
        }
        setUploadSuccessCount(function (prevState) {
          return prevState + successCount;
        });
        resolve();
      })["catch"](function (err) {
        console.error("file upload error", err);
      });
    });
  };
  var sendRequestsPromise = function sendRequestsPromise(fileInSections, workspaceId) {
    var promise = Promise.resolve();
    fileInSections.forEach(function (fileSections) {
      promise = promise.then(function () {
        return sendFileForUpload(fileSections, workspaceId);
      });
    });
    return promise;
  };
  var onConfirmUpload = function onConfirmUpload(newSelectedFiles) {
    setUploadInProgress(true);
    var workspaceId = get(selectedSource, ["value"], 0);
    setSelectedFiles(newSelectedFiles);
    setUploadComplete(false);
    setUploadSuccessCount(0);
    var chunkSize = 3; // this many files to uploads at a time
    var filesArray = values(newSelectedFiles);
    var fileInSections = chunk(filesArray, chunkSize);

    // add patientid to store if only single patient is being uploaded for auto selecting patient after registration
    var uniquePatients = uniqBy(filesArray, function (file) {
      return get(file, ["metaData", "PatientID"], "");
    });
    var newPatientId = "";
    if (uniquePatients && uniquePatients.length === 1) {
      newPatientId = get(uniquePatients, ["0", "metaData", "PatientID"], "");
    }
    Store.update(function (s) {
      s.newPatientId = newPatientId;
    });
    sendRequestsPromise(fileInSections, workspaceId).then(function () {
      setTimeout(function () {
        setUploadInProgress(false);
        setUploadComplete(true);
        setUploadSuccessCount(0);
        if (hasCurrentPatient) {
          trackEvent(MIXPANEL_EVENTS.ADD_SCANS_FOR_PATIENT, {
            patient_uid: get(currentPatient, ["patient_uid"], "")
          });
        } else {
          uniquePatients.forEach(function () {
            trackEvent(MIXPANEL_EVENTS.SUBMIT_ADD_PATIENT, {
              hasScan: true
            });
          });
        }
      }, 1000);
    });
  };
  var onRetryUpload = function onRetryUpload() {
    var filesList = Object.values(selectedFiles);
    var filteredList = filesList.filter(function (file) {
      return !file.uploadComplete;
    });
    var newFiles = filteredList.reduce(function (current, nextFile) {
      var fileKey = get(nextFile, ["fileKey"], "");
      set(nextFile, ["uploadProgress"], 0);
      return merge(current, _defineProperty({}, fileKey, nextFile));
    }, {});
    onConfirmUpload(newFiles);
  };
  var renderUploadModal = function renderUploadModal() {
    return /*#__PURE__*/React.createElement(UploadModal, {
      onClose: onClose,
      onConfirmUpload: onConfirmUpload,
      selectedSource: selectedSource,
      setSelectedSource: setSelectedSource,
      currentPatient: currentPatient,
      hasCurrentPatient: hasCurrentPatient,
      isUploadInProgress: isUploadInProgress,
      patient: patient,
      setPatient: setPatient
    });
  };
  var renderUploadProgressModal = function renderUploadProgressModal() {
    return /*#__PURE__*/React.createElement(UploadProgressModal, {
      selectedFiles: selectedFiles,
      totalFilesCount: totalFilesCount,
      uploadSuccessCount: uploadSuccessCount
    });
  };
  var renderUploadCompleteModal = function renderUploadCompleteModal() {
    return /*#__PURE__*/React.createElement(UploadCompleteModal, {
      selectedFiles: selectedFiles,
      onClose: onClose,
      onRetry: onRetryUpload,
      hasCurrentPatient: hasCurrentPatient
    });
  };
  var activePopup = function () {
    if (!isUploadPopupOpen && isUploadInProgress) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, isMobile && renderUploadModal(), renderUploadProgressModal());
    }
    if (uploadComplete) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, isMobile && renderUploadModal(), renderUploadCompleteModal());
    }
    return renderUploadModal();
  }();
  return isOpen ? activePopup : null;
};
export default DicomUpload;